import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { IconSearch, IconTrash } from "@tabler/icons-react";
import { Button, Card, Group, Input, PaginadorTabla, Select, Stack, Text, useConfirmDialog } from "@/components/ui";
import { useServerPagination } from "@/lib/hooks/use-server-pagination";
import { organizacionesAdminQueryOptions } from "@/features/admin-organizaciones/hooks/use-organizaciones-admin";
import {
  listarClavesSatCentrosCosto,
  type ClaveSatCentroCosto,
} from "../services/clave-sat-centro-costo-service";
import { useClavesSatCentrosCosto } from "../hooks/use-clave-sat-centro-costo";
import { useEliminarMasivoClavesSatCentrosCosto } from "../hooks/use-clave-sat-centro-costo-mutations";
import { ClavesSatCentrosCostoHeader } from "./ClavesSatCentrosCostoHeader";
import { ClaveSatCentroCostoCrearForm } from "./ClaveSatCentroCostoCrearForm";
import { ClavesSatCentrosCostoTable } from "./ClavesSatCentrosCostoTable";

interface Filtros {
  busqueda?: string;
  numeroSociedad?: string;
}

const TODAS_LAS_SOCIEDADES = "__todas__";

/**
 * Gestor de la relación "clave producto/servicio SAT → centro de costo por sociedad". Container
 * thin · orquesta `modoCrear`, la búsqueda + filtro de sociedad + paginado SERVER-SIDE (primera
 * página precargada por el loader del route) y la selección para la eliminación masiva.
 */
export function AdminClavesSatCentrosCostoPage() {
  const [modoCrear, setModoCrear] = useState(false);
  const p = useServerPagination<Filtros>({});
  const { data: sociedades = [] } = useQuery(organizacionesAdminQueryOptions);

  const serverParams = useMemo(
    () => ({
      ...(p.filtrosDebounced.busqueda?.trim() ? { busqueda: p.filtrosDebounced.busqueda.trim() } : {}),
      ...(p.filtrosDebounced.numeroSociedad ? { numeroSociedad: p.filtrosDebounced.numeroSociedad } : {}),
      page: p.page,
      pageSize: p.pageSize,
    }),
    [p.filtrosDebounced, p.page, p.pageSize]
  );
  const { data } = useClavesSatCentrosCosto(serverParams);
  const relaciones = data?.items ?? [];
  const total = data?.total ?? 0;

  // Selección múltiple (persiste entre páginas · los ids son estables).
  const [seleccionadas, setSeleccionadas] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setSeleccionadas((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleTodasPagina = () =>
    setSeleccionadas((prev) => {
      const n = new Set(prev);
      const todas = relaciones.length > 0 && relaciones.every((r) => n.has(r.id));
      for (const r of relaciones) {
        if (todas) n.delete(r.id);
        else n.add(r.id);
      }
      return n;
    });

  const confirm = useConfirmDialog();
  const eliminar = useEliminarMasivoClavesSatCentrosCosto({ onEliminado: () => setSeleccionadas(new Set()) });
  const confirmarEliminar = async () => {
    const n = seleccionadas.size;
    const ok = await confirm({
      title: `Eliminar ${n} ${n === 1 ? "relación" : "relaciones"}`,
      message:
        "Se ELIMINARÁN de forma permanente. Los prorrateos ya armados conservan su centro de costo (guardan el código, no la relación). Si se elimina el centro de costo automático de una clave, la bandera pasa al siguiente por código.",
      confirmLabel: "Eliminar seleccionadas",
      confirmColor: "peligro",
      icon: <IconTrash size={14} />,
    });
    if (ok) eliminar.mutate([...seleccionadas]);
  };

  const obtenerTodas = async (): Promise<ClaveSatCentroCosto[]> => {
    const filtros = {
      ...(p.filtrosDebounced.busqueda?.trim() ? { busqueda: p.filtrosDebounced.busqueda.trim() } : {}),
      ...(p.filtrosDebounced.numeroSociedad ? { numeroSociedad: p.filtrosDebounced.numeroSociedad } : {}),
    };
    const todas: ClaveSatCentroCosto[] = [];
    for (let pagina = 1; ; pagina++) {
      const r = await listarClavesSatCentrosCosto({ ...filtros, page: pagina, pageSize: 100 });
      todas.push(...r.items);
      if (todas.length >= r.total || r.items.length === 0) break;
    }
    return todas;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <ClavesSatCentrosCostoHeader total={total} modoCrear={modoCrear} onIniciarCrear={() => setModoCrear(true)} />
      <Stack gap="md">
        {modoCrear ? (
          <ClaveSatCentroCostoCrearForm sociedades={sociedades} onSalir={() => setModoCrear(false)} />
        ) : null}
        <Group gap="sm" align="flex-end">
          <Input
            placeholder="Buscar por clave SAT, centro de costo o sociedad…"
            leftSection={<IconSearch size={14} stroke={1.7} />}
            value={p.filtros.busqueda ?? ""}
            onChange={(e) => p.patchFiltros({ busqueda: e.currentTarget.value })}
            wrapperClassName="w-full max-w-90"
          />
          <Select
            aria-label="Filtrar por sociedad"
            options={[
              { value: TODAS_LAS_SOCIEDADES, label: "Todas las sociedades" },
              ...sociedades.map((o) => ({ value: o.numeroSociedad, label: `${o.numeroSociedad} · ${o.nombre}` })),
            ]}
            value={p.filtros.numeroSociedad ?? TODAS_LAS_SOCIEDADES}
            onValueChange={(v) => p.patchFiltros({ numeroSociedad: v === TODAS_LAS_SOCIEDADES ? undefined : v })}
          />
        </Group>
        {seleccionadas.size > 0 && (
          <Group
            justify="space-between"
            className="rounded-md border border-peligro-400/40 bg-peligro-600/5 px-3 py-2"
          >
            <Text size="sm" fw={500}>
              {seleccionadas.size} {seleccionadas.size === 1 ? "relación seleccionada" : "relaciones seleccionadas"}
            </Text>
            <Group gap="xs">
              <Button variant="subtle" size="xs" onClick={() => setSeleccionadas(new Set())}>
                Limpiar selección
              </Button>
              <Button
                size="xs"
                className="bg-peligro-600 hover:bg-peligro-700"
                leftSection={<IconTrash size={14} />}
                loading={eliminar.isPending}
                onClick={confirmarEliminar}
              >
                Eliminar seleccionadas
              </Button>
            </Group>
          </Group>
        )}
        <PaginadorTabla cargando={!data} total={total} page={p.page} pageSize={p.pageSize} onPageChange={p.setPage} variante="superior" />
        <Card padding="lg">
          <ClavesSatCentrosCostoTable
            cargando={!data}
            relaciones={relaciones}
            obtenerFilasExport={obtenerTodas}
            seleccionadas={seleccionadas}
            onToggle={toggle}
            onToggleTodasPagina={toggleTodasPagina}
          />
        </Card>
        <PaginadorTabla cargando={!data} total={total} page={p.page} pageSize={p.pageSize} onPageChange={p.setPage} sustantivo="relaciones" />
      </Stack>
    </div>
  );
}
